/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable consistent-return */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, set, clone } from 'lodash';
import { bindActionCreators } from 'redux';
import Pluralize from 'react-pluralize';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { Condition, FinanceType } from '@upstart/harmony/lib/constants';
import penActions from '../../redux/pen/actions';
import dealerActions from '../../redux/dealers/actions';
import ModifyBundleModal from './ModifyBundleModal';
import DefaultNewBundle from './DefaultNewBundle';
import LoadingOverlay from '../LoadingOverlay';
import ModifyBundleOrder from './ModifyBundleOrder';
import CopyBundleButton from './CopyBundle/CopyBundleButton';
import { selectionTypeInStore, selectionTypeOnline } from './cellFormatters';

function ProtectionPlansPenBundles(props) {
  const { pen, actions, dealer } = props;

  const processBundles = useCallback(
    () => ({
      [Condition.NEW]: {
        [FinanceType.LEASE]: get(
          dealer,
          `penConfig.bundles[${Condition.NEW}][${FinanceType.LEASE}]`,
          [],
        ),
        [FinanceType.PURCHASE]: get(
          dealer,
          `penConfig.bundles[${Condition.NEW}][${FinanceType.PURCHASE}]`,
          [],
        ),
        [FinanceType.CASH]: get(
          dealer,
          `penConfig.bundles[${Condition.NEW}][${FinanceType.CASH}]`,
          [],
        ),
      },
      [Condition.USED]: {
        [FinanceType.PURCHASE]: get(
          dealer,
          `penConfig.bundles[${Condition.USED}][${FinanceType.PURCHASE}]`,
          [],
        ),
        [FinanceType.CASH]: get(
          dealer,
          `penConfig.bundles[${Condition.USED}][${FinanceType.CASH}]`,
          [],
        ),
      },
      [Condition.CPO]: {
        [FinanceType.PURCHASE]: get(
          dealer,
          `penConfig.bundles[${Condition.CPO}][${FinanceType.PURCHASE}]`,
          [],
        ),
        [FinanceType.CASH]: get(
          dealer,
          `penConfig.bundles[${Condition.CPO}][${FinanceType.CASH}]`,
          [],
        ),
      },
    }),
    [dealer],
  );

  const [bundles, setBundles] = useState(processBundles);
  const [multiBundlesInDeal, setMultiBundlesInDeal] = useState(false);

  useEffect(() => {
    setMultiBundlesInDeal(get(pen, 'penConfig.multiBundlesInDeal', false));
  }, [pen]);

  useEffect(() => {
    setBundles(processBundles);
  }, [dealer, processBundles]);

  const createBundle = (condition, financeType) => {
    const bundle = clone(DefaultNewBundle);
    bundle.productIds = [];
    ModifyBundleModal({
      bundle,
      actions,
      dealer,
      pen,
      condition,
      financeType,
      bundles,
    });
  };

  const orderBundle = (bundleGroup, condition, financeType) => {
    const bundle = clone(DefaultNewBundle);
    bundle.productIds = [];
    ModifyBundleOrder({
      actions,
      dealer,
      pen,
      condition,
      financeType,
    });
  };

  const editBundle = (bundle, condition, financeType) => {
    ModifyBundleModal({
      bundle,
      actions,
      dealer,
      pen,
      condition,
      financeType,
      bundles,
    });
  };

  const bundleAction = (_cell, row, opts) => {
    const purchaseType = {
      condition: opts.condition,
      financeType: opts.financeType,
    };
    return (
      <div>
        <button
          className="btn btn-primary"
          onClick={() =>
            editBundle(row, purchaseType.condition, purchaseType.financeType)
          }
        >
          Edit
        </button>
        <CopyBundleButton
          sourceBundle={{ ...row, purchaseType }}
          dealer={dealer}
          updateDealer={actions.updateDealerRequest}
        />
      </div>
    );
  };

  const displayProductNames = (_cell, row) => {
    if (row.productIds && row.productIds.length) {
      return row.productIds
        .map((id) => pen.penConfig.productMap?.[id]?.displayName)
        .join(', ');
    }
    return '';
  };

  const displayDescription = (_cell, row) => {
    if (row.description !== undefined && row.description.length) {
      return `${row.description.substring(0, 200)}...`;
    }
  };

  const enableBundlesInUIToggle = () => {
    const { penConfig } = pen;
    set(penConfig, 'enableInUI.bundles', !pen.penConfig.enableInUI.bundles);
    actions.setPenConfigRequest({ penConfig });
  };

  const multiBundlesInDealToggle = () => {
    const { penConfig } = pen;
    setMultiBundlesInDeal(!multiBundlesInDeal);
    set(penConfig, 'multiBundlesInDeal', !multiBundlesInDeal);
    actions.setPenConfigRequest({ penConfig });
  };

  const renderBundleGroup = (bundleGroup, condition, financeType) => (
    <div className="bundle-group-container">
      <h3>
        <Pluralize
          className={'capitalize'}
          singular={`Bundles for ${condition} - ${financeType}`}
          plural={`Bundles for ${condition} - ${financeType}`}
          count={bundleGroup?.length}
        />
      </h3>
      <button
        className="btn btn-primary btn-large btn-large-default"
        onClick={() => createBundle(condition, financeType)}
      >
        Add Bundle
      </button>
      <button
        className="btn btn-primary btn-large btn-large-default ml-10"
        onClick={() => orderBundle(bundleGroup, condition, financeType)}
        disabled={!bundleGroup.length}
      >
        Edit Order
      </button>
      <BootstrapTable
        data={bundleGroup}
        striped
        hover
        bodyContainerClass="list-body-container"
        tableStyle={{ border: 'none' }}
      >
        <TableHeaderColumn dataField="name" dataSort isKey>
          Name
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="products"
          dataSort
          dataFormat={displayProductNames}
        >
          Products
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="description"
          dataSort
          dataFormat={displayDescription}
        >
          Description
        </TableHeaderColumn>
        <TableHeaderColumn dataField="enabled" width="90" dataSort>
          Enabled
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="selectedInStore"
          width="145"
          dataSort
          dataFormat={selectionTypeInStore}
        >
          In-Store selection
        </TableHeaderColumn>

        <TableHeaderColumn
          dataField="selectedOnline"
          dataSort
          dataFormat={selectionTypeOnline}
        >
          Online selection
        </TableHeaderColumn>

        <TableHeaderColumn
          dataField="action"
          width="200"
          dataFormat={bundleAction}
          formatExtraData={{ condition, financeType }}
        >
          Actions
        </TableHeaderColumn>
      </BootstrapTable>
    </div>
  );

  if (!pen.penConfig) {
    return;
  }

  return (
    <LoadingOverlay active={pen.fetching}>
      <div>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group fancy-checkbox">
              <input
                type="checkbox"
                name="allowBundledProductPurchase"
                id="allowBundledProductPurchase"
                autoComplete="off"
                value={get(pen, 'penConfig.enableInUI.bundles', true)}
                checked={get(pen, 'penConfig.enableInUI.bundles', true)}
                onChange={() => enableBundlesInUIToggle()}
              />
              <div className="btn-group">
                <label
                  htmlFor="allowBundledProductPurchase"
                  className="btn btn-primary"
                >
                  <span className="glyphicon glyphicon-ok" />
                  <span />
                </label>
                <label
                  htmlFor="allowBundledProductPurchase"
                  className="btn btn-default active"
                >
                  Allow Bundled Product Purchases
                </label>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group fancy-checkbox">
              <input
                type="checkbox"
                name="allowMultiBundlesInDeal"
                id="allowMultiBundlesInDeal"
                autoComplete="off"
                value={multiBundlesInDeal}
                checked={multiBundlesInDeal}
                onChange={() => multiBundlesInDealToggle()}
              />
              <div className="btn-group">
                <label
                  htmlFor="allowMultiBundlesInDeal"
                  className="btn btn-primary"
                >
                  <span className="glyphicon glyphicon-ok" />
                  <span />
                </label>
                <label
                  htmlFor="allowMultiBundlesInDeal"
                  className="btn btn-default active"
                >
                  Allow Multiple Bundles Purchased in a Deal
                </label>
              </div>
            </div>
          </div>
        </div>
        {renderBundleGroup(
          bundles[Condition.NEW][FinanceType.LEASE],
          Condition.NEW,
          FinanceType.LEASE,
        )}
        {renderBundleGroup(
          bundles[Condition.NEW][FinanceType.PURCHASE],
          Condition.NEW,
          FinanceType.PURCHASE,
        )}
        {renderBundleGroup(
          bundles[Condition.NEW][FinanceType.CASH],
          Condition.NEW,
          FinanceType.CASH,
        )}
        {renderBundleGroup(
          bundles[Condition.USED][FinanceType.PURCHASE],
          Condition.USED,
          FinanceType.PURCHASE,
        )}
        {renderBundleGroup(
          bundles[Condition.USED][FinanceType.CASH],
          Condition.USED,
          FinanceType.CASH,
        )}
        {renderBundleGroup(
          bundles[Condition.CPO]?.[FinanceType.PURCHASE],
          Condition.CPO,
          FinanceType.PURCHASE,
        )}
        {renderBundleGroup(
          bundles[Condition.CPO]?.[FinanceType.CASH],
          Condition.CPO,
          FinanceType.CASH,
        )}
      </div>
    </LoadingOverlay>
  );
}

ProtectionPlansPenBundles.propTypes = {
  pen: PropTypes.object,
  actions: PropTypes.object,
  dealer: PropTypes.object,
};

const mapStateToProps = ({ pen }) => ({
  pen,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...penActions,
      ...dealerActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProtectionPlansPenBundles);
